import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Icon, close, thumbs_up } from '../../components/Icon/Icon';
import { Modal } from '../../components/Modal/Modal';
import { OrderEvents } from '../../components/OrderEvents/OrderEvents';
import { OrderItems } from '../../components/OrderItems/OrderItems';
import { useConfig } from '../../config';
import { useFormatting, useTranslation } from '../../i18n/i18n';
import { Res, useApi } from '../../state/api';
import { ApiOrder } from '../../types';
import { AccountPageNavigation } from '../AccountPageNavigation/AccountPageNavigation';
import { ContentCard } from '../ContentCard/ContentCard';
import { getSecondsLeftToEdit, getTimeLeftString, hasTimeLeft } from '../editOrder/utils';
import { AddressSummary } from '../Form/AddressSummary';
import { Button, ButtonLink } from '../Form/Button';
import { Layout } from '../Layout/Layout';
import { Main } from '../Main/Main';
import { ReturnProductModal } from '../modals/ReturnProductModal/ReturnProductModal';
import { OrderReturnList } from '../ReturnOrdersList/OrderReturnList';
import { Routing } from '../Routes';
import { renderTemplate } from '../utils';
import { ErrorPage } from './ErrorPage';
import { LoaderPage } from './LoaderPage';
import './OrderPage.css';

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const COMPANY_RETURN_CLAIM_MAX_AGE_DAYS = 380;
const PRIVATE_RETURN_CLAIM_MAX_AGE_DAYS = 745;

function canCreateReturnClaim(order: ApiOrder): boolean {
	const createOrderEvent = order.events.find((event) => event.status === 1);
	if (!createOrderEvent) return false;

	const createdAt = new Date(createOrderEvent.dateCreated);
	if (Number.isNaN(createdAt.getTime())) return false;

	const maxAgeDays =
		order.customer.type === 'private' ? PRIVATE_RETURN_CLAIM_MAX_AGE_DAYS : COMPANY_RETURN_CLAIM_MAX_AGE_DAYS;

	return Date.now() - createdAt.getTime() <= maxAgeDays * DAY_IN_MS;
}

export const Order = (): JSX.Element => {
	const location = useLocation<{ displayId: string; updated: boolean }>();
	const locationState = location.state;
	type UrlParams = { orderId: string };
	const { orderId } = useParams<UrlParams>();
	const { data, error } = useApi(Res.order, { orderId });

	const { asPrice } = useFormatting();
	const { api, locale } = useConfig();
	const showReturnForm = true;
	const { data: orderReturns } = useApi(showReturnForm ? Res.orderReturns : null, { orderId });
	const history = useHistory();
	const t = {
		...useTranslation().orderPages,
		paymentType: useTranslation().paymentType,
		shippingType: useTranslation().shippingType,
		urls: useTranslation().routes,
		cart: useTranslation().cart,
		return: useTranslation().returnOrder,
	};
	const [timeLeft, setTimeLeft] = useState('');
	const [order, setOrder] = useState<ApiOrder | undefined>();
	const [returnProductModalIsOpen, setReturnProductModalIsOpen] = useState(false);
	useEffect(() => {
		setOrder(data);
	}, [data]);

	const tRoutes = useTranslation().routes;

	const resetUpdatedState = () => {
		if (locationState) {
			locationState.updated = false;
			history.replace({ ...history.location });
		}
	};

	const [fullyShipped, partShipped] = useMemo(() => {
		let fullyShipped = false;
		let partShipped = false;

		if (data) {
			data.events.forEach((event) => {
				if (event.status == 3) {
					fullyShipped = true;
				} else if (event.status == 8) {
					partShipped = true;
				}
			});
		}

		return [fullyShipped, partShipped];
	}, [data]);

	async function handleCancelOrder() {
		const confirmRes = window.confirm(t.cancelOrderConfirm);
		if (confirmRes) {
			const res = await axios.post(`${api}me/orders/${orderId}/cancel.json`, null, { withCredentials: true });
			if (res.data.success) {
				history.push({
					pathname: tRoutes.yourOrders,
					state: { cancelOrderSuccess: true },
				});
			}
		}
	}

	useEffect(() => {
		let timer = setInterval(() => {
			if (order && order.subscriptionOrder) {
				setTimeLeft(getTimeLeftString(order, t, locale));
			}
		}, 1000);
		return () => {
			clearInterval(timer);
		};
	}, [order]);

	if (error) return <ErrorPage />;
	if (!order) return <LoaderPage />;

	const {
		id,
		shippingType,
		paymentType,
		paymentDescription,
		delivery,
		billing,
		items,
		events,
		editable,
		totals: { subTotal, shippingFee, vat, total, notificationFee, letterFee, discount, discountTitle },
		subscriptionOrder,
		status,
	} = order;
	const canDownloadReceipt = ['cc', 'kco3', 'paypal', 'paypal_kustom'].includes(String(paymentType));
	const canCancelOrder = status === 1;
	const canEditOrder = canCancelOrder && editable && hasTimeLeft(getSecondsLeftToEdit(order), subscriptionOrder);
	const showRefundCard = showReturnForm && !canCancelOrder && canCreateReturnClaim(order);

	return (
		<Main>
			<AccountPageNavigation />
			<div className="order-page">
				{subscriptionOrder && (
					<ContentCard className="green">
						<i>{t.createdViaSubscription}</i>
						{timeLeft && (
							<>
								<br />
								<br />
								{renderTemplate(t.timeLeftCounter, { timeLeft })}
							</>
						)}
					</ContentCard>
				)}
				<ContentCard className="orderSummary">
					<h1>{`${t.order} ${locationState?.displayId ? locationState.displayId : id}`}</h1>
					<div className="summaryGrid">
						<div>
							{t.deliveryMethod}: <strong>{t.shippingType[shippingType]}</strong>
						</div>
						<div>
							{(fullyShipped || partShipped) && (
								<ButtonLink
									linkTo={`${t.urls.yourOrders}/${id}/${t.urls.fulfillment}`}
									className="secondary"
								>
									{t.followDelivery}
								</ButtonLink>
							)}
						</div>
						<div>
							{t.paymentMethod}:{' '}
							{paymentType && <strong>{paymentDescription || t.paymentType[paymentType]}</strong>}
						</div>
						<div>
							{['billogram', 'billogram_prepaid', 'arvato', 'arvato_prepaid'].includes(paymentType) && (
								<ButtonLink
									linkTo={`/me/orders/${id}/invoice.pdf`}
									target="_blank"
									className="secondary"
								>
									{t.invoice}
								</ButtonLink>
							)}

							{canDownloadReceipt && (
								<ButtonLink
									linkTo={`/me/orders/${id}/receipt.pdf`}
									className="secondary"
									target="_blank"
								>
									{t.receipt}
								</ButtonLink>
							)}
						</div>
					</div>
				</ContentCard>
				<Layout type="two-pane">
					<ContentCard title={t.deliveryAddress} className="order-address-summary">
						<AddressSummary address={delivery} />
					</ContentCard>
					<ContentCard title={t.invoiceAddress} className="order-address-summary">
						<AddressSummary address={billing} />
					</ContentCard>
				</Layout>
				{showReturnForm && orderReturns && <OrderReturnList orderReturns={orderReturns} />}
				{showRefundCard && (
					<ReturnProductModal
						open={returnProductModalIsOpen}
						setOpen={() => {}}
						onClose={() => setReturnProductModalIsOpen(false)}
						orderItems={data?.items || []}
						orderId={orderId}
					/>
				)}

				<h2>{t.products}</h2>
				<OrderItems orderItems={items}></OrderItems>

				<Layout type="two-pane">
					<div className="order-actions">
						<ContentCard title={t.reorder} className="green">
							<p>{t.pressButtonBelow}</p>
							<ButtonLink
								linkTo={`${tRoutes.reorder}/${id}?title=${locationState ? locationState.displayId : id}`}
								className="reorder-button"
							>
								{t.reorder}
							</ButtonLink>
						</ContentCard>
						<ContentCard
							title={t.cancelOrder}
							className={canCancelOrder ? 'green' : 'order-action-disabled'}
						>
							<p>{canCancelOrder ? t.cancelOrderDescription : t.cancelOrderUnavailableDescription}</p>
							<Button onClick={handleCancelOrder} disabled={!canCancelOrder}>
								{t.cancelOrder}
							</Button>
						</ContentCard>
						{canEditOrder && (
							<ContentCard title={t.editOrder} className="green">
								<p>{t.editOrderDescription}</p>
								<ButtonLink
									linkTo={`${tRoutes.yourOrders}/${id}${tRoutes.edit}`}
									className="reorder-button"
								>
									{t.editOrder}
								</ButtonLink>
							</ContentCard>
						)}
						{showRefundCard && (
							<ContentCard title={t.return.notSatisfied} className="green refund-card">
								<p>{t.return.returnInfo}</p>
								<Button onClick={() => setReturnProductModalIsOpen(true)}>
									{t.return.openReturnModal}
								</Button>
							</ContentCard>
						)}
					</div>
					<ContentCard className="subtotal-content-card">
						<table>
							<tbody>
								<tr>
									<td>{t.subTotal}</td>
									<td className="text-right">{asPrice(subTotal)}</td>
								</tr>
								<tr>
									<td>{t.shipping}</td>
									<td className="text-right">{asPrice(shippingFee)}</td>
								</tr>
								{discount !== 0 && (
									<tr className="discount-code">
										<td>{discountTitle}</td>
										<td className="text-right">{asPrice(discount)}</td>
									</tr>
								)}
								{notificationFee > 0 && (
									<tr>
										<td>{t.cart.notificationFee}</td>
										<td className="text-right">{asPrice(notificationFee)}</td>
									</tr>
								)}
								{letterFee > 0 && (
									<tr>
										<td>{t.cart.letterFee}</td>
										<td className="text-right">{asPrice(letterFee)}</td>
									</tr>
								)}
								<tr>
									<td>{t.vat}</td>
									<td className="text-right">{asPrice(vat)}</td>
								</tr>
								<tr>
									<td>{t.total}</td>
									<td className="text-right">{asPrice(total)}</td>
								</tr>
							</tbody>
						</table>
					</ContentCard>
				</Layout>

				<h2 className="history-heading">{t.history}</h2>
				<OrderEvents orderEvents={events}></OrderEvents>

				{!!locationState?.updated && (
					<Modal className="create-order-modal" show={locationState?.updated} onDismiss={() => ({})}>
						<ContentCard className="create-order-content">
							<button className="close-btn" onClick={resetUpdatedState}>
								<Icon shape={{ ...close, fill: 'black' }} size={20} />
							</button>

							<div className="create-order-content-main">
								<span className="create-order-icon">
									<Icon shape={thumbs_up} size={44} />
								</span>
								<p className="create-order-title">{t.modal.statusTitle}</p>
								<div>
									<Button onClick={resetUpdatedState}>{t.modal.ok}</Button>
								</div>
							</div>
						</ContentCard>
					</Modal>
				)}
			</div>
		</Main>
	);
};

export const orderRouting: Routing = {
	path(urls) {
		return `${urls.yourOrders}/:orderId`;
	},
	component: Order,
	requiresAuth: true,
};
